import { BASEURL } from "../BaseUrl";

/**
 * Creates a new workflow with the provided data.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data for creating the workflow.
 * @param {string} data.workflowName - The name of the workflow.
 * @param {string} data.userId - The ID of the user creating the workflow.
 * @param {string} data.userName - The name of the user creating the workflow.
 * @param {string} data.connectionType - The type of connection for the workflow.
 * @param {string} data.connectionName - The name of the connection for the workflow.
 * @param {string} data.execution_mode - The execution mode of the workflow.
 * @param {string} data.process_type - The type of process for the workflow.
 * @param {string} data.cloudEnvironment - The cloud environment for the workflow.
 * @param {string} data.engagement_id - The ID of the engagement associated with the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createWorkflow = async (axios, data) => {
  return await axios.post(`${BASEURL}/workflow/add-workflow`, {
    workflowName: data.workflowName,
    userId: data.userId,
    userName: data.userName,
    connectionType: data.connectionType,
    connectionName: data.connectionName,
    execution_mode: data.execution_mode,
    process_type: data.process_type,
    cloudEnvironment: data.cloudEnvironment,
    engagement_id: data.engagement_id,
  });
};

/**
 * Fetches a workflow by its ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow to fetch.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getWorkflowById = async (axios, workflow_id) => {
  return await axios.get(`${BASEURL}/workflow/get-workflow-by-id`, {
    params: { workflow_id },
  });
};

/**
 * Fetches a list of workflows based on the process type.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} process_type - The type of process to filter workflows by.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getWorkflowList = async (axios, process_type) => {
  return await axios.get(`${BASEURL}/workflow/get-workflow-list`, {
    params: { process_type },
  });
};

/**
 * Fetches all workflows based on various filters.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The parameters to filter workflows.
 * @param {string} data.user_id - The ID of the user to filter workflows by.
 * @param {string} data.process_type - The type of process to filter workflows by.
 * @param {string} data.client - The client to filter workflows by.
 * @param {string} data.client_id - The ID of the client to filter workflows by.
 * @param {string} data.engagement - The engagement to filter workflows by.
 * @param {string} data.engagement_id - The ID of the engagement to filter workflows by.
 * @param {string} data.search - The search term to filter workflows by.
 * @param {string} data.searchParam - The search parameter to use for filtering.
 * @param {string} data.status - The status to filter workflows by.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getAllWorkflows = async (axios, data) => {
  return await axios.get(`${BASEURL}/workflow/get-workflows`, {
    params: {
      user_id: data.user_id,
      process_type: data.process_type,
      client: data.client,
      client_id: data.client_id,
      engagement: data.engagement,
      engagement_id: data.engagement_id,
      search: data.search,
      searchParam: data.searchParam,
      status: data.status,
    },
  });
};

/**
 * Deletes a workflow by its ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow to delete.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const deleteWorkflow = async (axios, workflow_id) => {
  return await axios.delete(`${BASEURL}/workflow/delete-workflow`, {
    params: { workflow_id },
  });
};

/**
 * Refreshes a workflow by its ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} workflow_id - The ID of the workflow to refresh.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const refreshWorkflow = async (axios, workflow_id) => {
  return await axios.get(`${BASEURL}/workflow/refresh-workflow`, {
    params: { workflow_id },
  });
};

/**
 * Updates the status of a workflow.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The parameters to update the workflow status.
 * @param {string} data.workflow_id - The ID of the workflow to update.
 * @param {string} data.status - The new status of the workflow.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const workflowStatusChanged = async (axios, data) => {
  return await axios.put(`${BASEURL}/workflow/workflow-status-changed`, null, {
    params: {
      workflow_id: data.workflow_id,
      status: data.status,
    },
  });
};

export const WorkflowApi = {
  createWorkflow,
  getWorkflowById,
  getWorkflowList,
  getAllWorkflows,
  deleteWorkflow,
  refreshWorkflow,
  workflowStatusChanged,
};
